import { trackCtaClick } from '#utils/utils';
import {
  Badge,
  Box,
  Button,
  Container,
  HStack,
  Heading,
  Icon,
  List,
  ListIcon,
  ListItem,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import React from 'react';
import { FaCheckCircle, FaLock, FaSearch, FaShieldAlt } from 'react-icons/fa/index.js';
import { MdAccountBalance, MdOutlineSpeed, MdSecurity } from 'react-icons/md/index.js';

const MotionBox = motion(Box);

const ASSESSMENTS = [
  {
    id: 'operations',
    icon: MdOutlineSpeed,
    gradient: 'linear(to-br, blue.500, cyan.400)',
    title: 'Operations Assessment',
    subtitle: 'Find your highest-friction process.',
    description:
      'We map your current workflows end-to-end and identify exactly which manual processes are costing your team the most time and money. You leave with a written report of automation candidates, estimated time savings, and a recommended starting point.',
    includes: [
      'Workflow mapping session (60–90 min)',
      'Manual process audit',
      'Automation candidate ranking',
      'Written report with time-savings estimates',
      'Recommended package based on your situation',
    ],
    cta: 'Book Operations Assessment',
    href: '/contact-us?interest=operations-assessment',
    timeEstimate: '60–90 min',
    deliverable: 'Written workflow report',
  },
  {
    id: 'security',
    icon: MdSecurity,
    gradient: 'linear(to-br, red.500, orange.400)',
    title: 'Dark Web & Security Assessment',
    subtitle: 'Find out if your data is already compromised.',
    description:
      'We scan the dark web for your business domain, employee email addresses, and known credentials. You receive a report showing exactly what\'s exposed, how it got there, and what to do about it — before attackers act on it.',
    includes: [
      'Dark web scan for your business domain',
      'Compromised credential check',
      'Email exposure audit',
      'Written breach report',
      'Remediation priority list',
    ],
    cta: 'Get My Dark Web Report',
    href: '/contact-us?interest=dark-web-assessment',
    timeEstimate: '24–48 hr turnaround',
    deliverable: 'Written breach exposure report',
  },
  {
    id: 'spend',
    icon: MdAccountBalance,
    gradient: 'linear(to-br, green.500, teal.400)',
    title: 'Technology Bill Analysis',
    subtitle: 'Find savings in your current tech spend.',
    description:
      'Send us your current telecom, cloud, and software invoices. We\'ll benchmark your costs against our database of 200+ vendor contracts and identify exactly where you\'re overpaying — with specific alternatives and projected savings.',
    includes: [
      'Full telecom invoice audit',
      'Cloud and SaaS spend review',
      'Vendor pricing benchmark (200+ suppliers)',
      'Written savings opportunity report',
      'Vendor consolidation recommendations',
    ],
    cta: 'Analyze My Bills',
    href: '/contact-us?interest=bill-analysis',
    timeEstimate: '2–3 business days',
    deliverable: 'Written savings report',
  },
];

const AssessmentsPage = () => {
  const pageBg = useColorModeValue('gray.50', 'gray.950');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.900', 'white');
  const includeBg = useColorModeValue('gray.50', 'gray.900');

  return (
    <Box bg={pageBg}>
      {/* Hero */}
      <Box
        bgGradient="linear(to-br, gray.900, gray.800)"
        py={{ base: 20, md: 28 }}
        position="relative"
        overflow="hidden"
      >
        <Box position="absolute" top="-60px" right="-60px" w="350px" h="350px" borderRadius="full" bg="whiteAlpha.50" pointerEvents="none" />
        <Container maxW="container.lg" textAlign="center">
          <MotionBox initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 } as any}>
            <Badge bg="blue.500" color="white" fontSize="sm" px={4} py={1} borderRadius="full" mb={6} display="inline-block">
              Free Assessments
            </Badge>
            <Heading
              as="h1"
              fontSize={{ base: '3xl', md: '5xl' }}
              fontWeight="extrabold"
              color="white"
              lineHeight="1.1"
              letterSpacing="-0.03em"
              mb={6}
            >
              Find out exactly where
              <br />
              you're leaking time and money.
            </Heading>
            <Text fontSize="xl" color="whiteAlpha.700" maxW="2xl" mx="auto" mb={4}>
              Three free assessments. Each one delivers immediate, quantifiable value.
              No sales pitch. No obligation. Just a written report you can act on.
            </Text>
            <HStack justify="center" spacing={8} mt={8} flexWrap="wrap">
              {[
                { icon: FaCheckCircle, text: 'Written report included' },
                { icon: FaLock, text: 'No data retained after report' },
                { icon: FaSearch, text: 'No obligation to buy' },
              ].map((item) => (
                <HStack key={item.text} spacing={2}>
                  <Icon as={item.icon} color="green.400" boxSize={4} />
                  <Text fontSize="sm" color="whiteAlpha.700" fontWeight="medium">{item.text}</Text>
                </HStack>
              ))}
            </HStack>
          </MotionBox>
        </Container>
      </Box>

      {/* Assessment Cards */}
      <Box py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8}>
            {ASSESSMENTS.map((a, i) => (
              <MotionBox
                key={a.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 } as any}
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="2xl"
                overflow="hidden"
                boxShadow="lg"
                display="flex"
                flexDirection="column"
                _hover={{ boxShadow: '2xl', transform: 'translateY(-4px)' }}
                style={{ transition: 'all 0.3s ease' }}
              >
                {/* Gradient header */}
                <Box bgGradient={a.gradient} p={8} pb={6}>
                  <Icon as={a.icon} boxSize={10} color="white" mb={4} display="block" />
                  <Badge bg="whiteAlpha.300" color="white" fontSize="xs" px={3} py={1} borderRadius="full" mb={3}>
                    {a.timeEstimate}
                  </Badge>
                  <Heading as="h2" fontSize="xl" fontWeight="extrabold" color="white" mb={2}>
                    {a.title}
                  </Heading>
                  <Text fontSize="sm" color="whiteAlpha.800">{a.subtitle}</Text>
                </Box>

                {/* Body */}
                <VStack spacing={6} p={8} align="stretch" flex="1">
                  <Text fontSize="sm" color={textColor} lineHeight="1.7">
                    {a.description}
                  </Text>

                  <Box bg={includeBg} borderRadius="xl" p={5} border="1px solid" borderColor={borderColor}>
                    <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" color={textColor} mb={3}>
                      What's included
                    </Text>
                    <List spacing={2}>
                      {a.includes.map((item) => (
                        <ListItem key={item} fontSize="sm">
                          <HStack spacing={2}>
                            <ListIcon as={FaCheckCircle} color="green.400" />
                            <Text color={headingColor}>{item}</Text>
                          </HStack>
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  <Box mt="auto">
                    <HStack spacing={2} mb={4}>
                      <Icon as={FaShieldAlt} color="blue.400" boxSize={4} />
                      <Text fontSize="xs" color={textColor} fontWeight="medium">Deliverable: {a.deliverable}</Text>
                    </HStack>
                    <Button
                      as="a"
                      href={a.href}
                      colorScheme="blue"
                      size="md"
                      width="full"
                      onClick={() => trackCtaClick({ cta_label: a.cta, cta_location: 'Assessments Page', destination_url: a.href })}
                      _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                      transition="all 0.2s"
                    >
                      {a.cta}
                    </Button>
                  </Box>
                </VStack>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* What happens after */}
      <Box bg={useColorModeValue('blue.600', 'blue.900')} py={{ base: 14, md: 18 }}>
        <Container maxW="container.md" textAlign="center">
          <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="extrabold" color="white" mb={4}>
            What happens after your assessment.
          </Heading>
          <Text fontSize="md" color="whiteAlpha.800" mb={8} maxW="xl" mx="auto">
            You receive a written report. If you want to act on it, we walk you through
            the options. If you don't, you keep the report. No follow-up pressure.
          </Text>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {[
              { step: '1', text: 'You receive a written report within the stated timeframe' },
              { step: '2', text: 'We schedule a 30-min debrief to walk through the findings' },
              { step: '3', text: 'You decide if and when you want to move forward' },
            ].map((s) => (
              <Box key={s.step} bg="whiteAlpha.200" borderRadius="xl" p={6}>
                <Text fontSize="3xl" fontWeight="extrabold" color="yellow.300" lineHeight="1" mb={2} fontFamily="mono">{s.step}</Text>
                <Text fontSize="sm" color="whiteAlpha.800">{s.text}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>
    </Box>
  );
};

export default AssessmentsPage;
